import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import moment, { type Moment } from "moment";
import { type Override } from "../../../types/internal/utility";
import { history } from "../../_Bootstrap/RoutingAndStore";
import RetryButton from "../../Common/Buttons/RetryButton";
import CreationWizard from "../../Common/CreationWizard/CreationWizard";
import { type EditPageFieldValueType } from "../../Common/EditPage/EditPageField";
import NiceError from "../../Common/Errors/NiceError";
import LoadingScreen from "../../Common/Loading/LoadingScreen";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { convertFromGraphQLToIndexedDB } from "../../Utilities/Domain/Customers/ConvertCustomers";
import PageRuleScript from "../../Utilities/Domain/PageRules/PageRuleScript";
import Cloner from "../../Utilities/Generic/Cloner";
import { isValidEmail } from "../../Utilities/Generic/Strings";
import UUID from "../../Utilities/Generic/UUID";
import { CacheRequest } from "../../Utilities/Platform/Request";
import { upload } from "../../Workers/Upload/UploadAgent";
import { UPLOAD_TYPES } from "../../Workers/Upload/UploadEvents";
import CustomerEditForm from "./CustomerEditForm";
import { type Customer } from "./CustomerTypes";

// NOTE: Page rules have to be imported like this to prevent them from being transpiled and minified
let DefaultPageRules: {
    wizard: {
        customer: string;
    };
} = {
    wizard: {
        customer: "",
    },
};

import(
    /* webpackChunkName: "pagerules" */
    /* webpackMode: "lazy" */
    "../../Utilities/Domain/PageRules/DefaultPageRules"
)
    .then(def => DefaultPageRules = def.default);

const connector = connect();

interface Props extends ConnectedProps<typeof connector> {
    onComplete?: (customer: string) => void;
    onCancel?: () => void;
}

interface State {
    skipped: boolean;
    loading: boolean;
    rule: false | PageRuleScript;
    saving: boolean;
    saveError: boolean;
    fields: Override<Customer, {
        birthday: Moment | null;
        accountLimit: string;
        allowMarketingEmails: string;
    }>;
}

export class CustomerCreate extends React.Component<Props, State> {
    protected _loading = false;

    constructor(props: Props) {
        super(props);

        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.state = {
            skipped  : false,
            loading  : false,
            rule     : false,
            saving   : false,
            saveError: false,
            fields   : {
                firstName           : "",
                lastName            : "",
                code                : "",
                company             : "",
                businessNumber      : "",
                birthday            : null,
                gender              : "",
                emails              : [ "" ],
                fax                 : "",
                phone               : "",
                mobile              : "",
                allowMarketingEmails: "notSpecified",
                website             : "",
                twitter             : "",
                facebook            : "",
                billing             : {
                    addressStreet1 : "",
                    addressStreet2 : "",
                    addressSuburb  : "",
                    addressCity    : "",
                    addressPostcode: "",
                    addressCountry : "",
                    addressRegion  : "",
                },
                delivery: {
                    addressStreet1 : "",
                    addressStreet2 : "",
                    addressSuburb  : "",
                    addressCity    : "",
                    addressPostcode: "",
                    addressCountry : "",
                    addressRegion  : "",
                },
                invoiceMessage       : "",
                comments             : "",
                customerGroup        : null,
                loyaltyEnabled       : true,
                loyaltyRate          : null,
                loyaltyPoints        : 0,
                disablePromotions    : false,
                priceList            : null,
                accountLimit         : "",
                currentOwing         : 0,
                overrideCustomerGroup: false,
                requireOrderReference: false,
            },
        };

        this.handleBack = this.handleBack.bind(this);
        this.handleSkip = this.handleSkip.bind(this);
        this.handleFieldUpdate = this.handleFieldUpdate.bind(this);
        this.handleComplete = this.handleComplete.bind(this);
    }

    public componentDidMount(): void {
        void this.loadPageRule();
    }

    public componentWillUnmount(): void {
        if(this.state.rule) {
            this.state.rule.cleanup();
        }
    }

    protected async loadPageRule(): Promise<void> {
        if(this._loading) {
            return;
        }

        this._loading = true;

        this.setState({
            loading: true,
        });

        const source = await determineDataSource();
        let script;

        try {
            const rule = await source.pageRules.get("CUSTOMER_WIZARD");

            script = rule.rule;
        } catch(e) {
            // Revert to default rules
        }

        if(typeof script === "undefined" || script === null) {
            script = DefaultPageRules.wizard.customer;
        }

        const pageRule = new PageRuleScript(script);

        this.setState({
            rule   : pageRule,
            loading: false,
        });
    }

    protected handleBack(): void {
        if(typeof this.props.onCancel === "function") {
            this.props.onCancel();

            return;
        }

        history.push("/customers");
    }

    protected handleSkip(): void {
        // const customer = convertIndexedDBCustomerToGraphQL(this.state.fields);

        this.setState({
            // fields : customer,
            skipped: true,
        });
    }

    protected handleComplete(): Promise<void> {
        if(!this.state.skipped) {
            // Validate the email addresses - these are already validated by the edit form
            for(let i = 0, l = this.state.fields.emails.length; i < l; i++) {
                if(this.state.fields.emails[i].trim() === "") {
                    continue;
                }

                if(!isValidEmail(this.state.fields.emails[i])) {
                    PopupManager.error("An email address provided is invalid");

                    return Promise.resolve();
                }
            }
        }

        const id = UUID.generate();
        const fields = Cloner.Clone(this.state.fields) as any;

        if(moment.isMoment(this.state.fields.birthday)) {
            fields.birthday = this.state.fields.birthday.clone();
        }

        // Transform old data format to match new data format
        if(fields.first_name) fields.firstName = fields.first_name;

        if(fields.last_name) fields.lastName = fields.last_name;

        if(fields.customer_group) fields.customerGroup = fields.customer_group;

        let customer = convertFromGraphQLToIndexedDB(fields);
        const { fax, phone, mobile, website, twitter, facebook } = this.state.fields;

        customer.billing = {
            ...customer.billing,
            fax,
            phone,
            mobile,
            website,
            twitter,
            facebook,
        };

        /* eslint-disable @typescript-eslint/naming-convention */
        customer.client_id = id;
        customer.uuid = id;

        const fullName = [];

        if(customer.first_name) fullName.push(customer.first_name);

        if(customer.last_name) fullName.push(customer.last_name);

        customer.full_name = fullName.join(" ");

        if(!customer.full_name.trim()) {
            PopupManager.error("Please give the customer a name");

            return Promise.resolve();
        }

        this.setState({
            saving: true,
        });

        return determineDataSource()
            .then(async source => {
                if(customer.customer_group_id && !customer.override_customer_group) {
                    try {
                        const {
                            account_limit,
                            pricelist_id,
                            loyalty,
                            loyalty_rate,
                            disable_promotions,
                            allow_account_sales,
                            autoprint_account_receipt,
                            autoprint_receipt,
                            require_order_reference,
                        } = await source.customerGroups.get(customer.customer_group_id);

                        customer = {
                            ...customer,
                            account_limit,
                            pricelist_id,
                            loyalty,
                            loyalty_rate,
                            disable_promotions,
                            allow_account_sales,
                            autoprint_account_receipt,
                            autoprint_receipt,
                            require_order_reference,
                        };
                    } catch {
                        // Ignore
                    }
                }

                return source.customers.put(customer);
            })
            .then(() => upload(UPLOAD_TYPES.CUSTOMER, id))
            .then(newId => {
                if(!newId) {
                    newId = id;
                }

                PopupManager.success("Successfully created customer");

                // Clear the request cache as the customer is cached by default
                if(typeof CacheRequest.clearCache === "function") {
                    CacheRequest.clearCache();
                }

                if(this.props.onComplete) {
                    this.props.onComplete(newId);

                    return;
                }

                if(this.state.skipped) {
                    history.replace(`/customer/${newId}`);

                    return;
                }

                history.replace(`/customer/${newId}/edit`);
            });
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    protected async handleFieldUpdate(field: string, value: EditPageFieldValueType): Promise<void> {
        const split = field.split(".");

        this.setState((prevState: Readonly<State>) => {
            let update: Record<string, any> = {
                [field]: value,
            };

            if(split.length === 2) {
                const prevField = prevState.fields[split[0] as keyof State["fields"]];

                if(Array.isArray(prevField)) {
                    const index = parseInt(split[1]);

                    update = {
                        [split[0]]: [
                            ...prevField.slice(0, index),
                            value,
                            ...prevField.slice(index + 1),
                        ],
                    };
                } else {
                    update = {
                        [split[0]]: {
                            ...prevState.fields[split[0] as keyof State["fields"]] as any,
                            [split[1]]: value,
                        },
                    };
                }
            } else if(field === "customerGroup" && value === null) {
                update.overrideCustomerGroup = false;
            }

            return {
                fields: {
                    ...prevState.fields,
                    ...update,
                },
            };
        });
    }

    public render(): React.ReactNode {
        if(this.state.saving) {
            return (
                <LoadingScreen>
                    <p>We&apos;re saving the customer, do not leave this page.</p>
                </LoadingScreen>
            );
        }

        if(this.state.saveError) {
            return (
                <NiceError
                    icon="times"
                    title="Saving Error"
                    actions={[
                        <RetryButton
                            onClick={this.handleComplete}
                            key="0"
                        />,
                    ]}
                >
                    <p>We could not save the customer</p>
                </NiceError>
            );
        }

        if(this.state.skipped) {
            return (
                <CustomerEditForm
                    onClose={this.handleBack}
                    onChange={this.handleFieldUpdate}
                    onSave={this.handleComplete}
                    customer={this.state.fields}
                    floatSave={typeof this.props.onComplete !== "function"}
                />
            );
        }

        if(this.state.rule === false) {
            return <div className="creation-wizard" />;
        }

        return (
            <CreationWizard
                onBack={this.handleBack}
                onSkip={this.handleSkip}
                onComplete={this.handleComplete}
                onFieldChange={this.handleFieldUpdate}
                pageRule={this.state.rule}
            />
        );
    }
}

export default connector(CustomerCreate);
